import { SdkContext } from '../internal'
import type {
  NetworkingBadgeRule,
  NetworkingBookInput,
  NetworkingCard,
  NetworkingExploreParams,
  NetworkingJoinInput,
  NetworkingPagination,
  NetworkingParticipant,
  NetworkingProfile,
  NetworkingProgram,
  NetworkingReview,
  NetworkingSession,
  NetworkingSessionsTab,
  NetworkingSessionType,
  NetworkingStatus, NetworkingHostRule } from '@/types/networking'

export interface NetworkingPage<T> {
  items: T[]
  pagination: NetworkingPagination | null
}

/** `{ items, pagination }` whichever way the transport handed the list over. */
const pageOf = <T>(result: unknown): NetworkingPage<T> => {
  if (Array.isArray(result)) return { items: result as T[], pagination: null }
  const r = result as { items?: T[]; data?: T[]; pagination?: NetworkingPagination } | null
  return { items: r?.items || r?.data || [], pagination: r?.pagination ?? null }
}

/** Drops empty values: the query string carries only what the person chose. */
const clean = (query: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== false))

/**
 * F-176 Networking. Every route is `/networking/*` with the community from
 * X-CommunityId; the space travels as `spaceId`. Booking goes through the
 * booking core, so cancel and reschedule stay on `sdk.bookings`. The hours of a
 * host come from `sdk.scheduling.hostPage` (the same engine). Paths are written
 * by hand (F-03).
 */
export class NetworkingResource {
  constructor(private ctx: SdkContext) {}

  async config(spaceId: string): Promise<NetworkingProgram> {
    return this.ctx.call({ httpMethod: 'GET', path: '/networking/config' }, { query: { spaceId } })
  }

  async explore(params: NetworkingExploreParams): Promise<NetworkingPage<NetworkingCard>> {
    const { fields, ...rest } = params
    const picked = Object.fromEntries(Object.entries(fields || {}).filter(([, v]) => v))
    const query = clean({ ...rest, fields: Object.keys(picked).length ? JSON.stringify(picked) : undefined })
    return pageOf<NetworkingCard>(await this.ctx.raw({ httpMethod: 'GET', path: '/networking/explore' }, { query }))
  }

  async profile(memberId: string, spaceId: string): Promise<NetworkingProfile> {
    return this.ctx.call({ httpMethod: 'GET', path: '/networking/profiles/{memberId}' }, { memberId, query: { spaceId } })
  }

  async join(body: NetworkingJoinInput): Promise<NetworkingParticipant> {
    return this.ctx.call({ httpMethod: 'POST', path: '/networking/participants/me', hasBody: true }, { body })
  }

  async updateMine(body: { spaceId: string; offers?: string | null; seeks?: string | null; categoryKeys?: string[]; paused?: boolean; pausedUntil?: string | null }): Promise<NetworkingParticipant> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/networking/participants/me', hasBody: true }, { body })
  }

  /** Leaves the program; the API cancels the person's future sessions. */
  async leave(spaceId: string): Promise<NetworkingParticipant> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/networking/participants/me' }, { query: { spaceId } })
  }

  async pending(spaceId: string, page = 1): Promise<NetworkingPage<NetworkingParticipant>> {
    return pageOf(await this.ctx.raw({ httpMethod: 'GET', path: '/networking/participants/pending' }, { query: { spaceId, page, limit: 20 } }))
  }

  async participants(spaceId: string, params: { status?: NetworkingStatus[]; page?: number } = {}): Promise<NetworkingPage<NetworkingParticipant>> {
    return pageOf(await this.ctx.raw({ httpMethod: 'GET', path: '/networking/participants' }, {
      query: clean({ spaceId, status: params.status?.join(','), page: params.page ?? 1, limit: 20 })
    }))
  }

  async addParticipant(body: { spaceId: string; memberId: string; side?: 'HOST' | 'PARTICIPANT' }): Promise<NetworkingParticipant> {
    return this.ctx.call({ httpMethod: 'POST', path: '/networking/participants', hasBody: true }, { body })
  }

  async approve(participantId: string): Promise<NetworkingParticipant> {
    return this.ctx.call({ httpMethod: 'POST', path: '/networking/participants/{participantId}/approve', hasBody: true }, { participantId })
  }

  async reject(participantId: string, reason: string): Promise<NetworkingParticipant> {
    return this.ctx.call({ httpMethod: 'POST', path: '/networking/participants/{participantId}/reject', hasBody: true }, { participantId, body: { reason } })
  }

  async updateParticipant(participantId: string, body: { featured?: boolean; status?: 'ACTIVE' | 'PAUSED' | 'REMOVED'; side?: 'HOST' | 'PARTICIPANT' | 'BOTH' }): Promise<NetworkingParticipant> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/networking/participants/{participantId}', hasBody: true }, { participantId, body })
  }

  async sessions(params: { tab: NetworkingSessionsTab; spaceId?: string; page?: number }): Promise<NetworkingPage<NetworkingSession>> {
    return pageOf(await this.ctx.raw({ httpMethod: 'GET', path: '/networking/sessions' }, { query: clean({ ...params, limit: 20 }) }))
  }

  async session(bookingId: string): Promise<NetworkingSession> {
    return this.ctx.call({ httpMethod: 'GET', path: '/networking/sessions/{bookingId}' }, { bookingId })
  }

  async book(body: NetworkingBookInput): Promise<NetworkingSession> {
    return this.ctx.call({ httpMethod: 'POST', path: '/networking/sessions', hasBody: true }, { body })
  }

  async accept(bookingId: string): Promise<NetworkingSession> {
    return this.ctx.call({ httpMethod: 'POST', path: '/networking/sessions/{bookingId}/accept', hasBody: true }, { bookingId })
  }

  async decline(bookingId: string, reason: string): Promise<NetworkingSession> {
    return this.ctx.call({ httpMethod: 'POST', path: '/networking/sessions/{bookingId}/decline', hasBody: true }, { bookingId, body: { reason } })
  }

  async review(bookingId: string, body: { attended: boolean; rating?: number | null; comment?: string | null }): Promise<{ review: NetworkingReview; session: NetworkingSession }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/networking/sessions/{bookingId}/review', hasBody: true }, { bookingId, body })
  }

  async reviews(spaceId: string, memberId: string, page = 1): Promise<NetworkingPage<NetworkingReview> & { ratingCount: number; ratingAverage: number | null }> {
    const result = await this.ctx.raw({ httpMethod: 'GET', path: '/networking/reviews' }, { query: { spaceId, memberId, page, limit: 20 } })
    const page0 = pageOf<NetworkingReview>(result)
    const p = (page0.pagination ?? {}) as { ratingCount?: number; ratingAverage?: number | null }
    return { ...page0, ratingCount: p.ratingCount ?? 0, ratingAverage: p.ratingAverage ?? null }
  }

  async moderateReview(reviewId: string, body: { hidden: boolean; reason?: string }): Promise<NetworkingReview> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/networking/reviews/{reviewId}', hasBody: true }, { reviewId, body })
  }

  async sessionTypes(spaceId: string, memberId?: string): Promise<NetworkingSessionType[]> {
    return pageOf<NetworkingSessionType>(await this.ctx.raw({ httpMethod: 'GET', path: '/networking/session-types' }, { query: clean({ spaceId, memberId }) })).items
  }

  async createSessionType(body: { spaceId: string; title: string; description?: string | null; durationMinutes: number; requiresApproval?: boolean; active?: boolean }): Promise<NetworkingSessionType> {
    return this.ctx.call({ httpMethod: 'POST', path: '/networking/session-types', hasBody: true }, { body })
  }

  async updateSessionType(sessionTypeId: string, body: { title?: string; description?: string | null; durationMinutes?: number; requiresApproval?: boolean; active?: boolean }): Promise<NetworkingSessionType> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/networking/session-types/{sessionTypeId}', hasBody: true }, { sessionTypeId, body })
  }

  async deleteSessionType(sessionTypeId: string): Promise<void> {
    await this.ctx.call({ httpMethod: 'DELETE', path: '/networking/session-types/{sessionTypeId}' }, { sessionTypeId })
  }

  async setBadgeRules(body: { spaceId: string; firstSessionHosted?: string | null; firstSessionAttended?: string | null }): Promise<{ firstSessionHosted: NetworkingBadgeRule | null; firstSessionAttended: NetworkingBadgeRule | null }> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/networking/badges', hasBody: true }, { body })
  }

  /** F-176 (2026-10-29): who may ask to be a mentor; each list sent replaces the stored one. Owner and admin. */
  async setHostRule(body: { spaceId: string; groupIds?: string[]; tags?: string[]; badgeIds?: string[] }): Promise<NetworkingHostRule> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/networking/host-rule', hasBody: true }, { body })
  }
}
